import { Box, Card, CardBody, Heading, Icon, Stack, Text } from '@chakra-ui/react';
import { BatteryType, CargoType, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  aircraftRestrictionOptions,
  batteryTypeVariantOptions,
  getPrettyCargoType,
  packingGroupOptions,
  packingInstructionOptions,
  sectionOptions,
  unNumberOptions,
} from '@pelicargo/utils';
import { useWatch } from 'react-hook-form';
import { HiExclamationCircle } from 'react-icons/hi2';

import { SimpleDropzone } from '../SimpleDropzone';
import { SimpleNumberInput } from '../SimpleNumberInput';
import { SimpleRadioCardGroup } from '../SimpleRadioCardGroup';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleSwitch } from '../SimpleSwitch';

export const DangerousGoodsForm = () => {
  const cargoType: CargoType = useWatch({ name: 'cargo_type' });
  const batteryType: BatteryType = useWatch({ name: 'battery_type' });

  // Batteries isn't actually a part of CargoType but it's part of the UI
  const isDangerous = cargoType === CargoType.DangerousGoods;
  const isBattery = cargoType === CargoType.Batteries;
  const isDGBattery = isBattery && batteryType === BatteryType.DG_BATTERIES_IN_EQUIPMENT;
  const isDangerousOrDGBatt = isDangerous || isDGBattery;

  return (
    <Card>
      <CardBody>
        <Stack spacing="4">
          <Show if={isBattery && !batteryType}>
            <Stack direction="row" align="center" justify="center" py="8">
              <Icon as={HiExclamationCircle} boxSize="6" color="red.500" />
              <Text fontSize="xl">Please select a battery type.</Text>
            </Stack>
          </Show>

          <Stack
            direction={{ base: 'column', lg: 'row' }}
            align={{ base: 'flex-start', lg: 'center' }}
            justify="space-between"
            wrap="wrap"
          >
            <Show if={isDangerous || (isBattery && !!batteryType)}>
              <Heading size="md">{getPrettyCargoType(cargoType)}</Heading>
            </Show>
          </Stack>

          <Show if={batteryType === BatteryType.NON_RESTRICTED_BATTERIES_IN_EQUIPMENT}>
            <SimpleRadioCardGroup name="battery_type_variant" variant="unstyled" options={batteryTypeVariantOptions} />
          </Show>
          <Show if={isDangerousOrDGBatt}>
            <Box display="grid" gridTemplateColumns={{ base: 'repeat(2, auto)', md: 'repeat(4, auto)' }} gap={4}>
              <Show if={isDangerous}>
                <SimpleNumberInput
                  name="class"
                  label="Class"
                  placeholder="2-digit dec. e.g 1.1"
                  maxLength={2}
                  allowDecimals
                />
                <SimpleNumberInput
                  name="un_number"
                  label="UN #"
                  placeholder="4-digit #"
                  maxLength={4}
                  allowDecimals={false}
                />

                <SimpleSwitch
                  name="packing_group"
                  label="Packing Group"
                  variant="outline"
                  options={packingGroupOptions}
                />
              </Show>
              <Show if={isDGBattery}>
                <SimpleSelect name="un_number" label="UN #" options={unNumberOptions} />
                <SimpleSelect
                  name="packing_instruction"
                  label="Packing Instruction"
                  options={packingInstructionOptions}
                />
                <SimpleSwitch name="section" label="Section" variant="outline" options={sectionOptions} />
              </Show>

              <SimpleSwitch
                name="aircraft_restriction"
                label="Aircraft"
                variant="outline"
                options={aircraftRestrictionOptions}
              />
            </Box>

            <SimpleDropzone name="documents" documentType={DocumentType.DGD} isTypeSelectable />
          </Show>
        </Stack>
      </CardBody>
    </Card>
  );
};
